import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import PhoneInput, { isValidPhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Shared label style (used by the mapped fields and the phone field so they match)
const labelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#888',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: '4px',
  display: 'block',
};

const inputStyle = {
  padding: '0.55rem 0.75rem',
  borderRadius: '8px',
  fontSize: '14px',
  border: '1px solid #ddd',
  background: '#fafafa',
};

const LeadMagnetModal = ({ externalOpen, onExternalClose }) => {
  const [internalShow, setInternalShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(); // E.164 string (e.g. "+14155551234") or undefined
  const [phoneError, setPhoneError] = useState(false);

  // Show if either external trigger OR internal timer triggered it
  const show = externalOpen || internalShow;

  useEffect(() => {
    const hasSubmitted = localStorage.getItem('bfs_lead_magnet_submitted');
    if (hasSubmitted) return;

    const lastSeen = localStorage.getItem('bfs_lead_magnet_last_seen');
    const now = Date.now();
    const oneDayInMs = 24 * 60 * 60 * 1000;

    if (lastSeen && (now - parseInt(lastSeen)) < oneDayInMs) return;

    const timer = setTimeout(() => {
      const stillNotSubmitted = !localStorage.getItem('bfs_lead_magnet_submitted');
      if (stillNotSubmitted) {
        setInternalShow(true);
        localStorage.setItem('bfs_lead_magnet_last_seen', Date.now().toString());
      }
    }, 20000);

    return () => clearTimeout(timer);
  }, []);

  // Reset form when opened externally
  useEffect(() => {
    if (externalOpen) {
      setSubmitted(false);
      setAgreed(false);
      setName('');
      setEmail('');
      setPhone(undefined);
      setPhoneError(false);
    }
  }, [externalOpen]);

  const handleClose = () => {
    setInternalShow(false);
    if (onExternalClose) onExternalClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;

    // Validate the phone number (per-country) before sending.
    if (!phone || !isValidPhoneNumber(phone)) {
      setPhoneError(true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/leads/coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone }), // phone is E.164
      });
      if (response.ok) {
        localStorage.setItem('bfs_lead_magnet_submitted', 'true');
      } else {
        console.error('Submission failed:', await response.json());
      }
    } catch (error) {
      console.error('Network error:', error);
    } finally {
      setLoading(false);
      setSubmitted(true);
      setTimeout(() => handleClose(), 6000);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
      dialogClassName="bfs-lead-modal"
    >
      {/* Scoped styling so react-phone-number-input matches the other fields */}
      <style>{`
        .bfs-phone-field { display: flex; align-items: center; padding: 0.45rem 0.6rem; }
        .bfs-phone-field .PhoneInputInput {
          border: none; background: transparent; outline: none;
          font-size: 14px; padding: 0.1rem 0; width: 100%;
        }
        .bfs-phone-field .PhoneInputCountrySelect { cursor: pointer; }
      `}</style>

      <Modal.Body className="p-0 overflow-hidden" style={{ borderRadius: '16px' }}>
        <Row className="g-0" style={{ minHeight: '420px' }}>

          {/* Left dark panel */}
          <Col
            xs={12} md={4}
            style={{
              background: '#111',
              padding: '2rem 1.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            <div style={{ width: '32px', height: '3px', background: '#c8102e', borderRadius: '2px', marginBottom: '1rem' }} />
            <p style={{ color: '#aaa', fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 0.5rem', fontWeight: 500 }}>
              BFS Support
            </p>
            <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, lineHeight: 1.4, margin: '0 0 1.5rem' }}>
              You don't have to navigate this alone.
            </p>
            {[
              '14-Day calm protocol, free',
              'Free intro chat with a BFS coach',
              'No spam. Unsubscribe anytime.',
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#c8102e', marginTop: '5px', flexShrink: 0 }} />
                <p style={{ color: '#bbb', fontSize: '12px', margin: 0, lineHeight: 1.4 }}>{item}</p>
              </div>
            ))}
          </Col>

          {/* Right form panel */}
          <Col xs={12} md={8} style={{ padding: '2rem', position: 'relative', display: 'flex', flexDirection: 'column' }}>
            <button
              onClick={handleClose}
              style={{ position: 'absolute', top: '14px', right: '16px', border: 'none', background: 'none', fontSize: '1.3rem', color: '#bbb', cursor: 'pointer', lineHeight: 1, padding: '2px 8px' }}
            >
              ×
            </button>

            {!submitted ? (
              <>
                <p style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#c8102e', margin: '0 0 0.4rem' }}>
                  Struggling with BFS anxiety?
                </p>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1a1a1a', margin: '0 0 1.25rem', lineHeight: 1.35 }}>
                  Get the <span style={{ color: '#c8102e' }}>14-Day BFS Calm Protocol</span> — free
                </h2>

                <Form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    { label: 'Full name', type: 'text', placeholder: 'Jane Doe', value: name, onChange: e => setName(e.target.value) },
                    { label: 'Email address', type: 'email', placeholder: 'jane@example.com', value: email, onChange: e => setEmail(e.target.value) },
                  ].map(({ label, ...rest }) => (
                    <Form.Group key={label}>
                      <Form.Label style={labelStyle}>{label}</Form.Label>
                      <Form.Control {...rest} required style={inputStyle} />
                    </Form.Group>
                  ))}

                  {/* International phone field -> outputs E.164 into `phone` */}
                  <Form.Group>
                    <Form.Label style={labelStyle}>Phone number</Form.Label>
                    <div
                      className="bfs-phone-field"
                      style={{ ...inputStyle, padding: 0, borderColor: phoneError ? '#c8102e' : '#ddd' }}
                    >
                      <PhoneInput
                        international
                        defaultCountry="US"
                        placeholder="Enter phone number"
                        value={phone}
                        onChange={(val) => {
                          setPhone(val);
                          if (phoneError) setPhoneError(false);
                        }}
                      />
                    </div>
                    {phoneError && (
                      <span style={{ color: '#c8102e', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                        Please enter a valid phone number for the selected country.
                      </span>
                    )}
                  </Form.Group>

                  {/* Medical disclaimer checkbox */}
                  <div
                    onClick={() => setAgreed(!agreed)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      marginTop: '4px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      background: agreed ? '#fde8eb' : '#f7f7f7',
                      border: `1px solid ${agreed ? '#c8102e' : '#ddd'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '4px',
                      border: `2px solid ${agreed ? '#c8102e' : '#ccc'}`,
                      background: agreed ? '#c8102e' : 'white',
                      flexShrink: 0,
                      marginTop: '1px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}>
                      {agreed && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <p style={{ fontSize: '11px', color: '#555', margin: 0, lineHeight: 1.5 }}>
                      I understand this protocol is <strong>not medical advice</strong> and does not replace the guidance of a qualified healthcare professional. I have consulted or will consult my doctor before making any health changes.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !agreed}
                    style={{
                      marginTop: '4px',
                      padding: '0.75rem',
                      background: agreed ? '#c8102e' : '#ccc',
                      border: 'none',
                      borderRadius: '9999px',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: agreed ? 'pointer' : 'not-allowed',
                      transition: 'background 0.2s ease',
                    }}
                  >
                    {loading ? 'Sending...' : 'Send me the protocol'}
                  </Button>
                </Form>

                <p style={{ fontSize: '11px', color: '#bbb', marginTop: '1rem', lineHeight: 1.5 }}>
                  By submitting, you agree to receive the protocol and a follow-up from our BFS Health Coach. We respect your privacy.
                </p>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, textAlign: 'center' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#fde8eb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                  <span style={{ color: '#c8102e', fontSize: '1.5rem', fontWeight: 700 }}>✓</span>
                </div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Check your inbox!</h3>
                <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.6 }}>
                  The <strong>14-Day BFS Calm Protocol</strong> is on its way to <strong>{email}</strong>.
                </p>
                <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '0.75rem' }}>
                  A BFS Health Coach will reach out to <strong>{phone ? formatPhoneNumberIntl(phone) : ''}</strong> shortly.
                </p>
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default LeadMagnetModal;